import { watch } from "vue";
import type { ConsentCategories } from "~/layers/base/stores/cookieConsentStore";

/** Every consent category except `necessary`, which is always granted. */
export type GatedConsentCategory = Exclude<
	keyof ConsentCategories,
	"necessary"
>;

export interface ConsentCategoryHandlers {
	/**
	 * Runs when the category is granted — immediately if it already is.
	 * Called again on every re-grant when `onRevoke` is supplied, so keep it
	 * idempotent (guard on a script id or an "initialised" flag).
	 */
	onGrant: () => void;
	/**
	 * Runs when a previously granted category is withdrawn. Supplying it
	 * switches the watcher from fire-once to a permanent two-way sync.
	 */
	onRevoke?: () => void;
}

/**
 * Gate a side effect on one cookie-consent category (AGENTS.md rule #4).
 *
 * Two shapes:
 * - `useConsentCategory("marketing", cb)` — fires `cb` at most once, the
 *   first time the category is granted. For load-once vendor scripts.
 * - `useConsentCategory("statistics", { onGrant, onRevoke })` — keeps
 *   watching so withdrawal can be honoured too (stop a recorder, drop a
 *   script). Use this for SDKs that expose an off switch.
 *
 * Client-only: call from a `*.client.ts` plugin or a component `setup`. The
 * watcher is not bound to a component lifecycle, so a fire-once call from a
 * plugin lives for the session. Reads consent via the shared
 * `useCookieConsent()` store — never read the cookie directly.
 */
export const useConsentCategory = (
	category: GatedConsentCategory,
	handler: (() => void) | ConsentCategoryHandlers
): void => {
	const { categories } = useCookieConsent();
	const handlers: ConsentCategoryHandlers =
		typeof handler === "function" ? { onGrant: handler } : handler;
	const fireOnce = !handlers.onRevoke;

	if (categories.value[category]) {
		handlers.onGrant();
		if (fireOnce) return;
	}

	const stop = watch(
		() => categories.value[category],
		(granted) => {
			if (granted) {
				if (fireOnce) stop();
				handlers.onGrant();
				return;
			}
			handlers.onRevoke?.();
		}
	);
};
